import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { ShieldCheck, FileText, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  approved:   { label: 'Approved',    cls: 'bg-green-100 text-green-700 border-green-200', Icon: CheckCircle },
  rejected:   { label: 'Rejected',    cls: 'bg-red-100 text-red-600 border-red-200',       Icon: XCircle },
  in_process: { label: 'Under Review',cls: 'bg-blue-100 text-blue-700 border-blue-200',    Icon: Clock },
};

const CATEGORY_LABEL = {
  id_proof:       'ID Proof',
  address_proof:  'Address Proof',
};

const DocStatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.in_process;
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.cls}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
};

export default function PartnerDocuments() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/partner/kyc/documents`);
      setDocs(res.data || []);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDocs(); }, []);

  return (
    <DashboardLayout role={user?.role}>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">KYC Documents</h1>
            <p className="text-sm text-slate-500 mt-0.5">Your submitted verification documents and their review status</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchDocs} disabled={loading} data-testid="refresh-docs-btn"
            className="flex items-center gap-2 text-slate-600">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Docs Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : docs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="h-14 w-14 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <ShieldCheck className="h-7 w-7 text-slate-300" />
              </div>
              <p className="text-sm font-medium text-slate-500">No KYC documents submitted yet</p>
              <p className="text-xs text-slate-400 mt-1">
                Submit your KYC documents via the{' '}
                <a href="/partner/kyc-portal?tab=kyc" className="text-blue-600 hover:underline">KYC Portal</a>
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="kyc-docs-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide w-8">#</th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Category</th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Document Type</th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">File Name</th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Upload Date</th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {docs.map((doc, idx) => {
                    const reviewDate = doc.doc_reviewed_at
                      ? new Date(doc.doc_reviewed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                      : null;
                    return (
                      <tr key={doc.id} className="hover:bg-slate-50 transition-colors" data-testid={`doc-row-${idx}`}>
                        <td className="px-5 py-4 text-slate-400 text-xs">{idx + 1}</td>
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs font-medium">
                            <FileText className="h-3 w-3" />
                            {CATEGORY_LABEL[doc.category] || doc.category || '—'}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-slate-800 font-medium leading-snug">{doc.document_type || '—'}</p>
                          {doc.document_name && doc.document_name !== doc.filename && (
                            <p className="text-xs text-slate-400 mt-0.5">{doc.document_name}</p>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-slate-600 text-xs font-mono truncate max-w-[180px]" title={doc.filename}>{doc.filename || '—'}</p>
                          {doc.size && (
                            <p className="text-[10px] text-slate-400 mt-0.5">{(doc.size / 1024).toFixed(1)} KB</p>
                          )}
                        </td>
                        <td className="px-5 py-4 text-slate-500 text-xs whitespace-nowrap">
                          {doc.uploaded_at
                            ? new Date(doc.uploaded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                            : '—'}
                        </td>
                        <td className="px-5 py-4">
                          <div className="space-y-1.5">
                            <DocStatusBadge status={doc.doc_status || 'in_process'} />
                            {doc.doc_status === 'rejected' && doc.doc_rejection_reason && (
                              <p className="text-xs text-red-600 font-medium max-w-[200px]">
                                Reason: {doc.doc_rejection_reason}
                              </p>
                            )}
                            {reviewDate && (
                              <p className="text-[10px] text-slate-400">
                                Reviewed: {reviewDate}
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
